import { some } from 'lodash/fp';
import { useSelector } from 'react-redux';

import { Environment } from 'dashboard/models/environment';
import { RolePermission } from 'dashboard/models/role';
import { User } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import { Permission } from 'lib/permissions';

/**
 * @name `usePermission`
 *
 * A hook that returns if the currentUser has the passed in Permission.
 * This hook should rarely be used alone. Prefer to use the Permissioned
 * component instead.
 *
 */
const usePermission = (
  permission: Permission | Permission[],
  { global }: { global?: boolean } = {},
): boolean => {
  const user = useSelector<RootState, User>((state) => state.currentUser.currentUser);
  const currentEnvironmentId = useSelector<RootState, Environment>(
    (state) => state.currentEnvironment.currentEnvironment,
  )?.id;
  const permissions = typeof permission == 'string' ? [permission] : permission;

  if (user?.attributes?.['is-admin'] === true) {
    return true;
  }

  const rolePermissions: RolePermission[] =
    user?.attributes?.['role-permissions']?.filter((rp) => permissions.includes(rp.name)) ?? [];

  if (global) {
    /*
     * A role permission can be environment or organization global only if
     * it's not associated with a single object-id. For example,
     * inquiry-template.write allows editing templates when an object-id is
     * either set or unset. Only when it's unset, we also allow creation of
     * inquiry templates (apply to all current and future inquiry templates
     * will be checked in the roles page).
     */
    return (
      rolePermissions.length > 0 &&
      some(
        (rp) =>
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          rp['object-id'] == null && [currentEnvironmentId, null].includes(rp['environment-id']),
        rolePermissions,
      )
    );
  } else {
    /*
     * We don't look at object-id here because we only serialize the list of
     * objects the user can see. If the user has the Edit box checked in the
     * permissions page, they actually have permission to edit all of those
     * objects. If the box is not checked, no role permissions for that
     * environment would exist.
     */
    return (
      rolePermissions.length > 0 &&
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      some((rp) => [currentEnvironmentId, null].includes(rp['environment-id']), rolePermissions)
    );
  }
};

export default usePermission;
